import { useCallback, useEffect, useRef, useState } from "react";
import { ConversationEngine, type EngineState } from "../audio/ConversationEngine";
import { MAX_TURNS, DISCARD_AFTER_MS } from "@app/shared";
import type { Turn } from "@app/shared";

export type LiveTranscript = {
  turns: Turn[];
  partialUser: string;
  partialAssistant: string;
};

/** 無料枠を使い切ったときに fetchToken から投げる。UI で専用画面に切り替えるための目印。 */
class QuotaExceededError extends Error {
  constructor() {
    super("無料体験の回数を使い切りました");
    this.name = "QuotaExceededError";
  }
}

type StartResponse = {
  sessionId: string;
  token: string;
  apiVersion: string;
  setup: unknown;
};

export function useConversation(scenarioId: string) {
  const engineRef = useRef<ConversationEngine | null>(null);
  const hiddenAtRef = useRef<number | null>(null);
  // DO セッションの ID。POST /api/sessions のレスポンスで確定する。
  const sessionIdRef = useRef<string | null>(null);
  // セッション API（turn / hint / end）の送信を呼び出し順に直列化する。
  // turnComplete → ended の順で emit されるため、これで最後のターンが
  // /end より先に確実に記録される（DO が閉じてから届いて取りこぼすのを防ぐ）。
  const chainRef = useRef<Promise<unknown>>(Promise.resolve());

  const [state, setState] = useState<EngineState>("idle");
  const [turnCount, setTurnCount] = useState(0);
  const [hintsUsed, setHintsUsed] = useState(0);
  const [latencies, setLatencies] = useState<number[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [quotaExceeded, setQuotaExceeded] = useState(false);
  const [debugLog, setDebugLog] = useState<string[]>([]);
  const [needsResume, setNeedsResume] = useState(false);
  const [endReason, setEndReason] = useState<string | null>(null);
  const [transcript, setTranscript] = useState<LiveTranscript>({
    turns: [],
    partialUser: "",
    partialAssistant: "",
  });

  // セッション API を投げる。sessionId 未確定なら何もしない（無料枠オーバーで
  // DO が作られなかった場合など）。会話体験を止めないよう、失敗しても握りつぶす。
  const postSession = useCallback((path: string, body?: unknown) => {
    const id = sessionIdRef.current;
    if (!id) return;
    chainRef.current = chainRef.current.then(() =>
      fetch(`/api/sessions/${id}${path}`, {
        method: "POST",
        headers: body ? { "content-type": "application/json" } : undefined,
        body: body ? JSON.stringify(body) : undefined,
        // 画面を離れる瞬間の /end を取りこぼさない
        keepalive: true,
      }).catch((e) => console.error(`session ${path} 送信失敗`, e))
    );
  }, []);

  // エンジンは1度だけ生成する。state に入れると再レンダリングで壊れる。
  if (engineRef.current === null) {
    engineRef.current = new ConversationEngine({
      maxTurns: MAX_TURNS,
      // 短命トークンの取得と DO セッションの生成を1リクエストで行う。
      // 無料枠の検査もここ（サーバ側）で走る。
      // ※ AudioContext.resume() を先に済ませたいので、この fetch は
      //   engine.start() の中（音声初期化のあと）で呼ばれる設計を保つこと。
      fetchToken: async () => {
        const res = await fetch("/api/sessions", {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ scenarioId }),
        });
        if (res.status === 402) throw new QuotaExceededError();
        if (!res.ok) {
          const detail = await res.text();
          throw new Error(`セッションの開始に失敗しました: ${detail}`);
        }
        const data = (await res.json()) as StartResponse;
        sessionIdRef.current = data.sessionId;
        return { token: data.token, apiVersion: data.apiVersion, setup: data.setup };
      },
    });
  }
  const engine = engineRef.current;

  useEffect(() => {
    const offs = [
      engine.on("stateChange", setState),
      engine.on("turnCountChange", setTurnCount),
      engine.on("latency", (ms) => setLatencies((prev) => [...prev, ms])),
      engine.on("error", setError),
      engine.on("debug", (line) => {
        const stamp = new Date().toISOString().slice(11, 23);
        setDebugLog((prev) => [...prev.slice(-80), `${stamp} ${line}`]);
      }),
      engine.on("ended", (reason) => {
        setEndReason(reason);
        // 正常終了・切断いずれも DO を閉じて D1 に書き出す。
        // 送れなかった場合も DO の無操作タイマーが abandoned で回収する。
        postSession("/end");
      }),
      engine.on("turnComplete", (turn) => {
        setTranscript((prev) => ({
          turns: [...prev.turns, turn],
          partialUser: "",
          partialAssistant: "",
        }));
        // 文字起こしを DO へ。往復数のカウントもサーバ側で確定する。
        postSession("/turn", turn);
      }),
      engine.on("transcriptUpdate", (role, text) =>
        setTranscript((prev) =>
          role === "user"
            ? { ...prev, partialUser: text }
            : { ...prev, partialAssistant: text }
        )
      ),
    ];
    return () => offs.forEach((off) => off());
  }, [engine, postSession]);

  // iOS では他アプリに移ると AudioContext が interrupted になる。
  // 自動復帰に任せず、明示的な一時停止として見せる。
  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === "hidden") {
        hiddenAtRef.current = Date.now();
        return;
      }
      const away = hiddenAtRef.current ? Date.now() - hiddenAtRef.current : 0;
      hiddenAtRef.current = null;
      if (away > DISCARD_AFTER_MS) {
        engine.discardPendingAudio();
      }
      if (engine.isAudioSuspended()) {
        setNeedsResume(true);
      }
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, [engine]);

  useEffect(() => () => engine.dispose(), [engine]);

  const start = useCallback(async () => {
    setError(null);
    setQuotaExceeded(false);
    try {
      await engine.start();
    } catch (e) {
      if (e instanceof QuotaExceededError) {
        // 専用画面に切り替える。汎用エラーは出さない。
        setError(null);
        setQuotaExceeded(true);
        return;
      }
      setError(e instanceof Error ? e.message : String(e));
    }
  }, [engine]);

  const pressToTalk = useCallback(() => engine.pressToTalk(), [engine]);
  const releaseToTalk = useCallback(() => engine.releaseToTalk(), [engine]);
  const finish = useCallback(() => engine.finish("user"), [engine]);

  const resume = useCallback(async () => {
    const ok = await engine.resumeAudio();
    if (ok) setNeedsResume(false);
  }, [engine]);

  const useHint = useCallback(() => {
    setHintsUsed((n) => n + 1);
    postSession("/hint");
  }, [postSession]);

  return {
    state,
    turnCount,
    maxTurns: MAX_TURNS,
    hintsUsed,
    latencies,
    error,
    quotaExceeded,
    debugLog,
    needsResume,
    endReason,
    transcript,
    start,
    pressToTalk,
    releaseToTalk,
    finish,
    resume,
    useHint,
    getTranscript: () => engine.transcript,
    getSessionId: () => sessionIdRef.current,
  };
}
